/**
 * @fileoverview Statutory duties that surface in more than one review workspace
 * @module jobs/phases/review/duties
 *
 * Purpose:
 * - Model a statutory duty as the root record and each way it can be enforced
 *   as a "limb" hanging off it, so a practitioner browsing either workspace can
 *   see they are looking at two consequences of one duty rather than two
 *   unrelated claims.
 *
 * Insolvent trading is the case that motivated this: s588G creates the duty,
 * and the s588M compensation claim (Recoverable Transactions) and the
 * s1317E/s1317G civil penalty plus s588G(3) criminal offence (Civil & Criminal
 * Offences) are consequences of breaching it. Both workspaces already carry an
 * `insolvent_trading` head, but each reports the head provision as
 * "s588G / s588M", which reads as duplication.
 *
 * This is presentation-only reference data. The backend head configs keep
 * sending their own `provision` string (it feeds the deployed Anthropic agent
 * description, so changing it would force a managed-agent redeploy); the UI
 * substitutes the limb provision below wherever a duty entry exists.
 *
 * Used by:
 * - DutyContextBanner (the duty callout on both focused head views)
 * - MasterReviewTable / OffenceMasterTable (head subtitle)
 * - FocusedHeadView / OffenceFocusedHead (statute badge)
 */

/** The two review workspaces a duty limb can live in. */
export type DutyWorkspace = "recoverable" | "offences";

/** One way a duty can be enforced. */
export interface DutyLimb {
  /** Workspace this limb is reviewed in. */
  workspace: DutyWorkspace;
  /** Review agent slug, for `/dashboard/jobs/$jobId/review/$agent`. */
  agentSlug: string;
  /** Head key within that workspace, passed as the `head` search param. */
  headKey: string;
  /** Practitioner-facing workspace name, used on the cross-link. */
  workspaceLabel: string;
  /** Statutory hook shown in place of the head's own provision. */
  provision: string;
  /** What kind of claim this is, e.g. "Compensation claim". */
  claimType: string;
  /** Who is able to bring it. */
  broughtBy: string;
}

/** A statutory duty and every consequence of breaching it. */
export interface Duty {
  /** The provision that creates the duty itself. */
  provision: string;
  /** Practitioner-facing duty name. */
  name: string;
  limbs: ReadonlyArray<DutyLimb>;
}

/**
 * Duty to prevent insolvent trading.
 *
 * s588G is the duty. Breaching it exposes a director to three separate
 * consequences, which is why the same conduct is reviewed in two workspaces.
 * The quantum is one recovery assessed twice, not two recoveries.
 */
export const INSOLVENT_TRADING_DUTY: Duty = {
  provision: "s588G",
  name: "Duty to prevent insolvent trading",
  limbs: [
    {
      workspace: "recoverable",
      agentSlug: "flagged-transactions",
      headKey: "insolvent_trading",
      workspaceLabel: "Recoverable Transactions",
      provision: "s588M",
      claimType: "Compensation claim (civil, compensatory)",
      broughtBy:
        "The liquidator, in the company's name. A creditor may only bring it with the liquidator's written consent (ss588R-588U).",
    },
    {
      workspace: "offences",
      agentSlug: "civil-criminal-offences",
      headKey: "insolvent_trading",
      workspaceLabel: "Civil & Criminal Offences",
      provision: "s1317E / s1317G · s588G(3)",
      claimType: "Civil penalty and criminal offence",
      broughtBy:
        "ASIC brings the s1317E declaration and s1317G pecuniary penalty. The Commonwealth DPP brings the s588G(3) offence on referral, and it additionally requires dishonesty.",
    },
  ],
};

const DUTIES: ReadonlyArray<Duty> = [INSOLVENT_TRADING_DUTY];

/** A duty resolved from the perspective of one of its limbs. */
export interface ResolvedDuty {
  duty: Duty;
  /** The limb belonging to the workspace that asked. */
  thisLimb: DutyLimb;
  /** Every other consequence of the same duty. */
  otherLimbs: ReadonlyArray<DutyLimb>;
}

/**
 * Resolve the duty behind a head, from the point of view of one workspace.
 *
 * Returns null for the heads that are not consequences of a shared duty, which
 * is all of them bar insolvent trading — callers render their normal head
 * provision in that case.
 *
 * @example
 * const duty = dutyForHead("recoverable", "insolvent_trading");
 * duty?.thisLimb.provision; // "s588M"
 * duty?.otherLimbs[0].workspaceLabel; // "Civil & Criminal Offences"
 */
export function dutyForHead(
  workspace: DutyWorkspace,
  headKey: string,
): ResolvedDuty | null {
  for (const duty of DUTIES) {
    const thisLimb = duty.limbs.find(
      (l) => l.workspace === workspace && l.headKey === headKey,
    );
    if (!thisLimb) continue;
    return {
      duty,
      thisLimb,
      otherLimbs: duty.limbs.filter((l) => l !== thisLimb),
    };
  }
  return null;
}

/**
 * The head provision to display, preferring the limb-specific one.
 *
 * @param fallback - the head's own `provision` from the backend config.
 */
export function displayProvision(
  workspace: DutyWorkspace,
  headKey: string,
  fallback: string,
): string {
  return dutyForHead(workspace, headKey)?.thisLimb.provision ?? fallback;
}

/**
 * Master-table subtitle: the limb provision plus the duty it sits under, so
 * the relationship is visible without opening the head.
 */
export function displayProvisionWithDuty(
  workspace: DutyWorkspace,
  headKey: string,
  fallback: string,
): string {
  const resolved = dutyForHead(workspace, headKey);
  if (!resolved) return fallback;
  return `${resolved.thisLimb.provision} · duty ${resolved.duty.provision}`;
}
